import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css"; // Import Sun Editor's CSS File
import 'katex/dist/katex.min.css';
import SunEditorCore from "suneditor/src/lib/core";
import plugins from "suneditor/src/plugins";

import { Button, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';

import { EditorialTemplate, EditorialType } from '../../domain/domain';
import { createHeaderPlugin } from './headerPlugin';
import { TEXT_EDITOR_INITIALIZATION_DELAY_MS } from '../../api/common';
import { createLevelsPlugin } from './levelsPlugin';
import { createSymbolsTagsPlugin } from './symbolsTagsPlugin';
import { createTableSettingsDialogPlugin } from './tableSettingsDialogPlugin';
import { createTemplateEditorPlugin } from './templateEditorPlugin';
import { applyTableResize, normalizeTableAlignment, convertTableDivsToP, addColGroupsToTables } from '../../api/common';
import { useHotkeys } from 'react-hotkeys-hook';

import './style.css';

const convertTableCellDimensionsToPoints = (html: string): string => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const content = template.content;
    const tables = content.querySelectorAll('table');

    tables.forEach((table) => {
        const cells = table.querySelectorAll<HTMLTableCellElement>('td, th');
        cells.forEach((cell) => {
            const currentWidth = cell.style.width;
            if (currentWidth && currentWidth.includes('px')) {
                const widthPx = parseInt(currentWidth.replace('px', ''));
                const widthPt = Math.round(widthPx * 0.75);
                cell.style.width = `${widthPt}pt`;
            }

            const currentHeight = cell.style.height;
            if (currentHeight && currentHeight.includes('px')) {
                const heightPx = parseInt(currentHeight.replace('px', ''));
                const heightPt = Math.round(heightPx * 0.75);
                cell.style.height = `${heightPt}pt`;
            }
        });
    });

    return template.innerHTML;
};

const katex = require('katex');

export type SunEditorWrapperProps = {
    showToolbar: boolean,
    html: string,
    readOnly: boolean,
    showEditorialTemplates?: boolean,
    showEditorialTypes?: boolean,
    showEditorialButtons?: boolean,
    selectedEditorialType?: EditorialType,
    onChange: (newHtml: string) => void,
    templates: EditorialTemplate[],
    showResizingBar?: boolean
}

const SunEditorWrapper: React.FC<SunEditorWrapperProps> = ({
    showToolbar,
    readOnly,
    html,
    onChange,
    templates,
    showResizingBar = true
}) => {
    const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
    const [selectedCells, setSelectedCells] = useState<HTMLElement[]>([]);
    const editorRef = useRef<SunEditorCore>();
    const contextMenuRef = useRef<HTMLDivElement>(null);

    let header = createHeaderPlugin();
    let templateEditor = createTemplateEditorPlugin(templates);
    let tableSettingsDialog = createTableSettingsDialogPlugin();
    let symbolsTags = createSymbolsTagsPlugin();
    let levels = createLevelsPlugin();

    let buttonsList = [
        ["undo", "redo"],
        ['levels'],
        [
            "paragraphStyle",
            "bold",
            "underline",
            "italic",
            "strike",
            "subscript",
            "superscript",
        ],
        ["math"],
        ["horizontalRule"],
        ["header"],
        ["align", "list"],
        ["outdent", "indent"],
        ["table", "tableSettingsDialog"],
        ["symbolsTags"]
    ];

    if (templates.length > 0) {
        buttonsList.push(["template_editor"]);
    }

    const handleSelectionChange = () => {
        const editor = editorRef.current;
        if (!editor) return;

        // Detect multiple cell selections
        const selectedElements = Array.from(editor.getContext().element.wysiwyg.querySelectorAll('.se-table-selected-cell') || []);
        if (selectedElements.length > 1 && !readOnly) {
            setSelectedCells(selectedElements as HTMLElement[]);
            setIsContextMenuVisible(true);
            selectedElements.forEach((cell: any) => {
                cell.classList.add('multiple-selected-cell');
            });
        } else {
            const selectedCells = document.querySelectorAll('.multiple-selected-cell');

            selectedCells.forEach((cell) => {
                cell.classList.remove('multiple-selected-cell');
            });
            setIsContextMenuVisible(false);
        }
    };

    const handleAlignmentChange = (alignment: string) => {
        const getAlignmentValues = (combo: string): { textAlign: string; verticalAlign: string } => {
            const [vertical, horizontal] = combo.split('-');
            const verticalAlign = { top: 'top', middle: 'middle', bottom: 'bottom' }[vertical] || 'top';
            const textAlign = { left: 'left', center: 'center', right: 'right' }[horizontal] || 'left';
            return { textAlign, verticalAlign };
        };

        const { textAlign, verticalAlign } = getAlignmentValues(alignment);

        selectedCells.forEach(cell => {
            const innerElement = cell.querySelector('p, div') as HTMLElement | null;
            if (!innerElement) return;

            innerElement.setAttribute('data-cell-align', alignment);
            innerElement.style.textAlign = textAlign;
            cell.style.verticalAlign = verticalAlign;
        });

        setIsContextMenuVisible(false);

        const editorInstance = editorRef.current;
        editorInstance?.core.history.push(true);
    };

    const handleFormattingOption = (option: string) => {
        const editorInstance = editorRef.current;
        if (!editorInstance) return;

        selectedCells.forEach(cell => {
            const innerElement = cell.querySelector('p, div') as HTMLElement | null;
            if (!innerElement) return;

            let content = innerElement.innerHTML.trim();

            switch (option) {
                case 'bold':
                    // <strong> is used due to Suneditor requirement.
                    // EditorPanel.tsx functions parseHtmlForEditor & replaceStrongWithBold handle <strong>/<b> conversion.
                    content = content.includes('<strong>')
                        ? content.replace(/<strong>(.*?)<\/strong>/g, '$1')
                        : `<strong>${content}</strong>`;
                    break;
                case 'italic':
                    content = content.includes('<em>')
                        ? content.replace(/<em>(.*?)<\/em>/g, '$1')
                        : `<em>${content}</em>`;
                    break;
                default:
                    break;
            }
            innerElement.innerHTML = content;
        });

        selectedCells.forEach((cell) => {
            cell.classList.remove('multiple-selected-cell');
        });

        editorInstance.core.history.push(false);
        setIsContextMenuVisible(false);
    };

    useEffect(() => {
        document.addEventListener('selectionchange', handleSelectionChange);

        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, [selectedCells]);

    useLayoutEffect(() => {
        if (isContextMenuVisible && contextMenuRef.current && selectedCells.length > 0) {
            const contextMenu = contextMenuRef.current;
            const firstSelectedElement = selectedCells[selectedCells.length - 1];

            if (firstSelectedElement instanceof HTMLElement) {
                const rect = firstSelectedElement.getBoundingClientRect();
                contextMenu.style.top = `${rect.top + window.scrollY - 120}px`;
                contextMenu.style.left = `${rect.left + window.scrollX - 50}px`;
                contextMenu.style.display = 'block';
            }
        }
    }, [isContextMenuVisible, selectedCells]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isContextMenuVisible && contextMenuRef.current) {
                const target = event.target as Node;

                if (!contextMenuRef.current.contains(target)) {
                    const isSelectElement = target instanceof Element &&
                        target.closest('.MuiSelect-root, .MuiMenu-root, .MuiPaper-root, .MuiMenuItem-root');

                    if (!isSelectElement) {
                        setIsContextMenuVisible(false);
                        const selectedCells = document.querySelectorAll('.multiple-selected-cell');
                        selectedCells.forEach((cell) => {
                            cell.classList.remove('multiple-selected-cell');
                        });
                    }
                }
            }
        };

        if (isContextMenuVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isContextMenuVisible]);

    const getButtonAriaLabel = (command: string): string => {
        const ariaLabels: Record<string, string> = {
            undo: 'Undo',
            redo: 'Redo',
            levels: 'Levels',
            paragraphStyle: 'Paragraph style',
            bold: 'Bold',
            underline: 'Underline',
            italic: 'Italic',
            strike: 'Strikethrough',
            subscript: 'Subscript',
            superscript: 'Superscript',
            math: 'Math formula',
            horizontalRule: 'Horizontal rule',
            header: 'Header',
            align: 'Align',
            list: 'List',
            outdent: 'Decrease indent',
            indent: 'Increase indent',
            table: 'Table',
            tableSettingsDialog: 'Table settings',
            symbolsTags: 'Symbols and tags',
            template_editor: 'Template editor'
        };

        return ariaLabels[command] || command;
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            const toolbars = document.querySelectorAll('.se-toolbar');
            toolbars.forEach(toolbar => {
                if (toolbar) {
                    // Make all buttons keyboard focusable
                    const buttons = toolbar.querySelectorAll('button[data-command]');
                    buttons.forEach(button => {
                        button.setAttribute('tabindex', '0');

                        // Ensure proper ARIA label if not set
                        if (!button.getAttribute('aria-label')) {
                            const command = button.getAttribute('data-command');
                            if (command) {
                                button.setAttribute('aria-label', getButtonAriaLabel(command));
                            }
                        }
                    });
                }
            })
        }, TEXT_EDITOR_INITIALIZATION_DELAY_MS);

        return () => clearTimeout(timer);
    }, []);

    const applyCellVerticalAlign = (container: Element | HTMLElement) => {
        const htmlContainer = container as HTMLElement;
        const allCells = htmlContainer.querySelectorAll('td, th');

        allCells.forEach((cell) => {
            const cellElement = cell as HTMLElement;
            const p = cellElement.querySelector('p[data-cell-align]');

            if (p) {
                const dataAlign = p.getAttribute('data-cell-align');
                if (dataAlign) {
                    const match = dataAlign.match(/^(top|middle|bottom)/i);
                    if (match) {
                        const verticalAlign = match[0].toLowerCase();
                        cellElement.style.verticalAlign = verticalAlign;
                        const styleAttr = cellElement.getAttribute('style') || '';
                        if (!styleAttr.includes('vertical-align')) {
                            const newStyle = styleAttr.trim()
                                ? `${styleAttr.trim()}; vertical-align: ${verticalAlign}`
                                : `vertical-align: ${verticalAlign}`;
                            cellElement.setAttribute('style', newStyle);
                        }
                    }
                }
            }
        });
    };

    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            let hasNewTable = false;
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as HTMLElement;
                        const tables = element.tagName === 'TABLE'
                            ? [element as HTMLTableElement]
                            : element.querySelectorAll('table');

                        tables.forEach((table) => {
                            hasNewTable = true;
                            const hasProperWidth = table.style.width && table.style.width.includes('pt');

                            if (!hasProperWidth) {
                                const isLandscape = table.hasAttribute('data-print-landscape');
                                const defaultWidth = isLandscape ? 612 : 366;
                                table.setAttribute('style', `width: ${defaultWidth}pt !important;`);

                                const firstRow = table.querySelector('tr');
                                if (firstRow) {
                                    const cells = firstRow.querySelectorAll('td, th');
                                    if (cells.length > 0) {
                                        let columnCount = 0;
                                        const cellSpans: number[] = [];
                                        cells.forEach((cell) => {
                                            const colspan = parseInt(cell.getAttribute('colspan') || '1');
                                            cellSpans.push(colspan);
                                            columnCount += colspan;
                                        });

                                        if (columnCount > 0) {
                                            const existingColgroup = table.querySelector('colgroup');
                                            if (existingColgroup) {
                                                existingColgroup.remove();
                                            }

                                            const colgroup = document.createElement('colgroup');
                                            const singleColumnWidth = Math.floor(defaultWidth / columnCount);

                                            cellSpans.forEach((span) => {
                                                const col = document.createElement('col');
                                                const colWidth = singleColumnWidth * span;
                                                col.setAttribute('style', `width: ${colWidth}pt;`);
                                                if (span > 1) {
                                                    col.setAttribute('span', span.toString());
                                                }

                                                colgroup.appendChild(col);
                                            });

                                            if (table.firstChild) {
                                                table.insertBefore(colgroup, table.firstChild);
                                            } else {
                                                table.appendChild(colgroup);
                                            }
                                        }
                                    }
                                }
                            }
                        });
                    }
                });
            });

            if (hasNewTable && !readOnly && editorRef.current) {
                applyTableResize(() => {
                    if (editorRef.current) {
                        editorRef.current.core.history.push(true);
                        const editorElement = editorRef.current.core.context.element.wysiwyg;
                        if (editorElement) {
                            applyCellVerticalAlign(editorElement);
                        }
                        const event = new Event('input', { bubbles: true });
                        editorElement.dispatchEvent(event);
                    }
                });
            }

            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    const target = mutation.target;
                    if (target instanceof HTMLElement && (target.tagName === 'TH' || target.tagName === 'TD') && editorRef.current) {
                        const editorElement = editorRef.current.core.context.element.wysiwyg;
                        if (editorElement) {
                            applyCellVerticalAlign(editorElement);
                        }
                    }
                }
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as HTMLElement;
                            if (element.tagName === 'TH' || element.tagName === 'TD' || (element instanceof HTMLElement && element.querySelector('th, td'))) {
                                if (editorRef.current) {
                                    const editorElement = editorRef.current.core.context.element.wysiwyg;
                                    if (editorElement) {
                                        applyCellVerticalAlign(editorElement);
                                    }
                                }
                            }
                        }
                    });
                }
            });
        });

        const timer = setTimeout(() => {
            if (editorRef.current) {
                const editorElement = editorRef.current.core.context.element.wysiwyg;
                if (editorElement) {
                    observer.observe(editorElement, {
                        childList: true,
                        subtree: true
                    });
                }
            }
        }, TEXT_EDITOR_INITIALIZATION_DELAY_MS);

        return () => {
            observer.disconnect();
            clearTimeout(timer);
        };
    }, [readOnly]);

    useEffect(() => {
        if (editorRef.current) {
            const wysiwyg = editorRef.current.core.context.element.wysiwyg;
            if (wysiwyg) {
                setTimeout(() => {
                    applyCellVerticalAlign(wysiwyg);
                }, 100);
            }
        }
    }, [html]);

    useHotkeys('mod+k', (event) => {
        event.preventDefault();

        if (editorRef && editorRef.current) {
            const toolbar = editorRef.current.core.context.element.toolbar;
            const btnArray = toolbar.querySelectorAll('button');

            for (const btn of btnArray) {
                const button = btn as HTMLButtonElement;

                if (!button.disabled) {
                    button.focus();
                    break;
                }
            }
        }
    }, { enableOnContentEditable: true }, []);

    const handleChange = (content: string) => {
        let convertedContent = normalizeTableAlignment(content, true);
        convertedContent = convertTableDivsToP(convertedContent);
        convertedContent = addColGroupsToTables(convertedContent);
        convertedContent = convertTableCellDimensionsToPoints(convertedContent);

        setTimeout(() => {
            if (editorRef.current) {
                const editorElement = editorRef.current.core.context.element.wysiwyg;
                if (editorElement) {
                    applyCellVerticalAlign(editorElement);
                }
            }
        }, 0);

        onChange(convertedContent);
    };

    return (
        <div>
            <SunEditor
                getSunEditorInstance={(sunEditor: SunEditorCore) => {
                    editorRef.current = sunEditor;
                }}
                autoFocus={true}
                defaultValue={html}
                setContents={html}
                onChange={handleChange}
                onLoad={() => {
                    const editor = editorRef.current;
                    if (editor) {
                        const wysiwyg = editor.core.context.element.wysiwyg;
                        if (wysiwyg) {
                            applyCellVerticalAlign(wysiwyg);
                        }

                        if (!readOnly) {
                            applyTableResize(() => {
                                editor.core.history.push(true);
                                const content = editor.getContents(true);
                                if (content) {
                                    handleChange(content);
                                }
                            });
                        }
                    }
                }}
                hideToolbar={!showToolbar}
                disable={readOnly}
                onPaste={(e, cleanData) => {
                    e.preventDefault();
                    let modifiedHtml = normalizeTableAlignment(cleanData, true);
                    modifiedHtml = convertTableDivsToP(modifiedHtml);
                    modifiedHtml = addColGroupsToTables(modifiedHtml);
                    modifiedHtml = convertTableCellDimensionsToPoints(modifiedHtml);
                    return modifiedHtml;
                }}
                setOptions={{
                    buttonList: buttonsList,
                    plugins: [
                        plugins.table,
                        plugins.horizontalRule,
                        plugins.align,
                        plugins.list,
                        plugins.math,
                        plugins.paragraphStyle,
                        header,
                        templateEditor,
                        tableSettingsDialog,
                        symbolsTags,
                        levels
                    ],
                    minHeight: "300px",
                    resizingBar: showResizingBar,
                    paragraphStyles: [
                        {
                            name: "Double Indent",
                            class: "__se__p-double-indent",
                        },
                    ],
                    katex: {
                        src: katex,
                        options: {
                            output: "mathml"
                        },
                    },
                    showPathLabel: false,
                    hrItems: [
                        { name: 'Bleed Rule', style: 'width: 50%;' },
                        { name: 'Single Horizontal', style: 'border-style: solid;' },
                        { name: 'Double Horizontal', style: 'border-style: none; border-top: double;' },
                    ],
                    tabDisable: false,
                    addTagsWhitelist: "L1|L2|L3|L4|L5|L6|HEADING|T|t|PL|BL|DL_1|BL_1|colgroup|col|footnote",
                    pasteTagsBlacklist: "div|span|br",
                    attributesWhitelist: { all: "data-.+|valign|align", hr: 'class|style', td: "style|data-.+", tr: "style", div: "style|data-.+", p: "style|data-.+", table: 'style|data-print-landscape', colgroup: 'style', col: 'style|span|width' },
                    attributesBlacklist: { h1: "style", h2: "style", h3: "style", h4: "style", h5: "style", h6: "style" },
                    // Using a custom save handler, so we disable SunEditor's internal save shortcut to ensure Cmd + S exclusively triggers our custom handler.
                    shortcutsDisable: ['save'],
                }}
            />

            {isContextMenuVisible && (
                <div ref={contextMenuRef} className="custom-context-menu">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleFormattingOption('bold')}
                        >
                            Bold
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleFormattingOption('italic')}
                        >
                            Italic
                        </Button>
                    </div>

                    <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                        <InputLabel id="alignment-select-label" style={{ color: 'black' }}>Alignment</InputLabel>
                        <Select
                            labelId="alignment-select-label"
                            defaultValue=""
                            onChange={(e) => handleAlignmentChange(e.target.value as string)}
                            label="Alignment"
                        >
                            <MenuItem value="" disabled>Select Alignment</MenuItem>
                            <MenuItem value="top-left">Top Left</MenuItem>
                            <MenuItem value="top-center">Top Center</MenuItem>
                            <MenuItem value="top-right">Top Right</MenuItem>
                            <MenuItem value="middle-left">Center Left</MenuItem>
                            <MenuItem value="middle-center">Center</MenuItem>
                            <MenuItem value="middle-right">Center Right</MenuItem>
                            <MenuItem value="bottom-left">Bottom Left</MenuItem>
                            <MenuItem value="bottom-center">Bottom Center</MenuItem>
                            <MenuItem value="bottom-right">Bottom Right</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            )}
        </div>
    );
};

export default SunEditorWrapper;
